import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table } from "antd";
import LeaveBalanceModelPopup from "../../../components/modelpopup/LeaveBalanceModelPopup";
import { Link } from "react-router-dom";
import ReportingSelect from "../../../components/ReportingSelect";

const LeaveBalances = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const maximizeBtnRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(parseInt(localStorage.getItem("empid")) || null);
  const [selectedEmployeeLabel, setSelectedEmployeeLabel] = useState(localStorage.getItem("empName") || null);

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener('click', handleClick);

    return () => {
      maximizeBtn.removeEventListener('click', handleClick);
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };
  }, [isFullScreen]);

  const handleEmployeeChange = (selectedOption) => {
    if (selectedOption && selectedOption.value !== "all") {
      const { value, label } = selectedOption;
      setSelectedEmployee({ value, label });
      setSelectedEmployeeId(value);
      setSelectedEmployeeLabel(label);
      localStorage.setItem("defaultEmployeeId", value);
      localStorage.setItem("defaultEmployeeLabel", label);
  
      // Filter the data based on the selected employee
      const filtered = data.filter((item) => item.id === value);
      setFilteredData(filtered);
    } else {
      // Show all employees if "Show All" is selected
      setSelectedEmployee(null);
      setSelectedEmployeeId(null);
      setSelectedEmployeeLabel("Show All");
      localStorage.removeItem("defaultEmployeeId");
      localStorage.removeItem("defaultEmployeeLabel");
      setFilteredData(data); // Reset to the full data set
    }
  };
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${apiUrl}/leaves/leave-balance`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const leaveBalances = response.data.leaveBalances;

        if (leaveBalances.length > 0) {
          const firstRecord = leaveBalances[0];
          const leaveTypes = Object.keys(firstRecord).filter(
            (key) =>
              !key.endsWith("_id") &&
              !key.includes("_taken") &&
              key !== "empcode" &&
              key !== "name" &&
              key !== "gender" &&
              key !== "balance" &&
              key !== "id"
          );

          const dynamicColumns = [
            {
              title: "Employee Name",
              dataIndex: "name",
              key: "name",
              sorter: (a, b) => a.name.localeCompare(b.name),
              render: (text, record) => (
                <a
                  onClick={() => handleNameClick(record)}
                  data-bs-toggle="modal"
                  data-bs-target="#leave_balance_modal"
                  href="#"
                >
                  {text}
                </a>
              ),
            },
            {
              title: "Employee Code",
              dataIndex: "empcode",
              key: "empcode",
              sorter: (a, b) => a.empcode.localeCompare(b.empcode),
            },
            ...leaveTypes.flatMap((leaveType, index) => [
              {
                title: leaveType,
                children: [
                  {
                    title: "Available",
                    dataIndex: leaveType,
                    key: `${leaveType}_available`,
                    sorter: (a, b) => a[leaveType] - b[leaveType],
                    onCell: () => ({
                      style: {
                        backgroundColor: getColorByIndex(index),
                      },
                    }),
                    onHeaderCell: () => ({
                      style: {
                        backgroundColor: getColorByIndex(index),
                      },
                    }),
                  },
                  {
                    title: "Booked",
                    dataIndex: `${leaveType}_taken`,
                    key: `${leaveType}_booked`,
                    sorter: (a, b) => a[`${leaveType}_taken`] - b[`${leaveType}_taken`],
                    onCell: () => ({
                      style: {
                        backgroundColor: getColorByIndex(index),
                      },
                    }),
                    onHeaderCell: () => ({
                      style: {
                        backgroundColor: getColorByIndex(index),
                      },
                    }),
                  },
                ],
                onHeaderCell: () => ({
                  style: {
                    backgroundColor: getColorByIndex(index),
                  },
                }),
              },
            ]),
          ];

          setColumns(dynamicColumns);
        }

        setData(leaveBalances);
        setFilteredData(leaveBalances); // Set filtered data initially to all data
        setLoading(false);
      } catch (error) {
        console.error("Error fetching leave balance data:", error);
        setLoading(false); // Set loading to false on error
      }
    };

    fetchData();
  }, [apiUrl, token]);

  const getColorByIndex = (index) => {
    const colors = [
      "#FFEEEE", "#EEFFEE", "#EEEEFF", "#FFFFEE", "#EEFFFF", "#FFEEFF",
      "#FFFAF0", "#F0FFF0", "#F0F0FF", "#FFFFF0", "#F0FFFF", "#FFF0FF"
    ];
    return colors[index % colors.length];
  };

  const handleNameClick = (record) => {
    setSelectedEmployee(record);
    setModalVisible(true);
  };

  const handleSave = (updatedLeaveBalances) => {
    // Update the state with the new leave balances
    setData((prevData) =>
      prevData.map((employee) => {
        if (employee.id === selectedEmployee.id) {
          updatedLeaveBalances.forEach((updatedBalance) => {
            const leaveType = updatedBalance.leaveType;
            employee[leaveType] = updatedBalance.newBalance;
            employee[`${leaveType}_taken`] = updatedBalance.newBalance - employee[leaveType]; // Adjust this logic as necessary
          });
        }
        return employee;
      })
    );
    setModalVisible(false); // Hide the modal after saving
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-2">
              <h3 className="page-title">Leave Balances</h3>
            </div>

            <div className="col-md-2">
              <ReportingSelect
                value={selectedEmployeeId ? { value: selectedEmployeeId, label: selectedEmployeeLabel } : null}
                onChange={handleEmployeeChange}
              />
            </div>

            <div className="col-md-8">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <Link to="#" className="grid-view btn btn-link"><i className="las la-redo-alt" /></Link>
                  <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}><i className="las la-expand-arrows-alt" /></Link>
                </div>
              </div>
            </div>
          
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            {loading ? (
              <div id="loader-wrapper-">
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={filteredData} // Use filtered data for display
                  rowKey={(record) => record.id || record.empcode}
                />

              </div>
            )}
          </div>
        </div>
      </div>
      {selectedEmployee && (
        <LeaveBalanceModelPopup
          onSave={handleSave}
          leaveTypeData={selectedEmployee}
          editModalVisible={modalVisible}
        />
      )}
    </div>
  );
};

export default LeaveBalances;
